import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CalendarDays, UploadCloud, Trash2 } from "lucide-react";

export default function EventsView() {
  const [data, setData] = useState({ hasImage: false, uploadedAt: null, imageDataUrl: null });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try { setData(await api.getEventsCalendar()); } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      await api.uploadEventsCalendar(file);
      toast.success("Calendario de eventos actualizado");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al subir la imagen");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeImage = async () => {
    setBusy(true);
    try {
      await api.deleteEventsCalendar();
      toast.success("Calendario eliminado");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al eliminar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="events-view">
      <div>
        <span className="overline text-gold">Calendario mensual</span>
        <h2 className="text-2xl md:text-3xl font-heading font-bold">Calendario de Eventos</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Esta imagen se envía cuando cualquier miembro escribe <code>!events</code> en un grupo activo.
          También puedes actualizarla desde WhatsApp con <code>!setevents</code> (adjuntando la foto).
        </p>
      </div>

      <div className="card-surface p-6 space-y-4" data-testid="events-calendar-card">
        {data.hasImage ? (
          <div className="space-y-3">
            <img
              data-testid="events-calendar-preview"
              src={data.imageDataUrl}
              alt="Calendario de eventos"
              className="w-full max-w-md rounded-md border border-white/10 object-contain"
            />
            <div className="text-xs text-muted-foreground" data-testid="events-calendar-uploaded-at">
              Última actualización: {data.uploadedAt ? new Date(data.uploadedAt).toLocaleString("es-MX") : "—"}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-10 text-center text-muted-foreground border border-dashed border-white/15 rounded-md" data-testid="events-calendar-empty">
            <CalendarDays size={32} className="mb-2 opacity-60" />
            <p className="text-sm">Aún no hay calendario cargado.</p>
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          <label className="flex items-center gap-2 bg-gold text-black font-bold rounded-md px-4 py-2.5 text-sm hover:brightness-95 transition-all cursor-pointer disabled:opacity-60">
            <UploadCloud size={16} /> {busy ? "Subiendo…" : "Subir nueva imagen"}
            <input
              ref={fileRef}
              data-testid="events-calendar-upload-input"
              type="file"
              accept="image/*"
              className="hidden"
              disabled={busy}
              onChange={onFileChange}
            />
          </label>
          {data.hasImage && (
            <button
              data-testid="events-calendar-remove-btn"
              onClick={removeImage}
              disabled={busy}
              className="flex items-center gap-2 border border-crimson/40 text-crimson rounded-md px-4 py-2.5 text-sm hover:bg-crimson/10 transition-all disabled:opacity-60"
            >
              <Trash2 size={16} /> Quitar calendario
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
